import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { Failure } from '../sync/errors'

/**
 * The device's own copy of the till.
 *
 * Two stores doing two different jobs. `outbox` holds writes that have not
 * reached the server yet, intentions. `cache_*` holds the server's version of
 * the world, plus whatever those intentions have already changed locally.
 *
 * Firestore merged the two into one cache and that is exactly what made a
 * rejected write invisible: the row simply reverted and nobody was told.
 * Keeping them apart is what lets a refused sale stay on screen, in red,
 * instead of vanishing.
 */

export type SyncTable =
  | 'orders'
  | 'products'
  | 'categories'
  | 'pos_tables'
  | 'tenants'
  | 'admins'

/** A write waiting to reach the server. */
export interface OutboxOp {
  /** Auto-incremented. The whole ordering guarantee rests on this. */
  seq: number
  tenantId: string
  table: SyncTable
  /** id of the row this touches, the compaction key. */
  rowId: string
  /**
   * No 'delete'. Orders are voided rather than removed, and menu items are
   * deactivated; nothing in this app deletes a row it has to sync.
   */
  type: 'insert' | 'update'
  payload: Record<string, unknown>
  attempts: number
  createdAt: number
  /** Not attempted before this instant, backoff. */
  nextAttemptAt: number
  /**
   * 'failed' means the server refused it and waiting will not help. The op is
   * kept, not dropped: the sale it represents is still real and somebody has
   * to decide what happens to it.
   */
  status: 'pending' | 'inflight' | 'failed'
  lastError?: Failure
}

/**
 * A cached row.
 *
 * `server` is what the server last said. `local` is what unsent writes have
 * changed on top. What the screen shows is the two merged, so a round added
 * offline is visible immediately, and a stale server echo arriving later
 * cannot wipe it out.
 */
export interface CachedRow {
  id: string
  tenantId: string
  server: Record<string, unknown> | null
  local: Record<string, unknown> | null
  /** Outbox entries still to go for this row; empty means fully synced. */
  outboxSeqs: number[]
  /** Set when the server refused a write for this row. Drives the red state. */
  syncError?: Failure
  /** Denormalised out of the row so orders can be indexed by business day. */
  dayKey?: string
}

interface LocalDB extends DBSchema {
  outbox: {
    key: number
    value: OutboxOp
    indexes: {
      by_status_time: [string, number]
      by_row: [string, string]
    }
  }
  rows: {
    key: [SyncTable, string]
    value: CachedRow & { table: SyncTable }
    indexes: {
      by_table: SyncTable
      by_table_day: [SyncTable, string]
    }
  }
  meta: {
    key: string
    value: { key: string; value: unknown }
  }
}

const DB_NAME = 'kermes'
const DB_VERSION = 1

let dbPromise: Promise<IDBPDatabase<LocalDB>> | null = null

export function getDB(): Promise<IDBPDatabase<LocalDB>> {
  dbPromise ??= openDB<LocalDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      const outbox = db.createObjectStore('outbox', {
        keyPath: 'seq',
        autoIncrement: true,
      })
      // The drainer's only query: what is ready to send, oldest first.
      outbox.createIndex('by_status_time', ['status', 'nextAttemptAt'])
      // Answers "is this row still waiting?", the pending flag.
      outbox.createIndex('by_row', ['table', 'rowId'])

      const rows = db.createObjectStore('rows', { keyPath: ['table', 'id'] })
      rows.createIndex('by_table', 'table')
      rows.createIndex('by_table_day', ['table', 'dayKey'])

      db.createObjectStore('meta', { keyPath: 'key' })
    },
  })
  return dbPromise
}

/**
 * Whether this device can store anything at all.
 *
 * Safari in private browsing gives you an IndexedDB that throws on write. A
 * till that discovers this mid-service has been taking orders into a void, so
 * the app asks the question up front and refuses to pretend.
 */
export async function storageWorks(): Promise<boolean> {
  try {
    const db = await getDB()
    await db.put('meta', { key: '__probe', value: Date.now() })
    await db.delete('meta', '__probe')
    return true
  } catch (err) {
    console.error('[local] IndexedDB kullanılamıyor', err)
    return false
  }
}

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const db = await getDB()
  const row = await db.get('meta', key)
  return row === undefined ? fallback : (row.value as T)
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  const db = await getDB()
  await db.put('meta', { key, value })
}

/** What the screen should show for a row: server truth under local intent. */
export function viewOf(row: CachedRow): Record<string, unknown> {
  return { ...(row.server ?? {}), ...(row.local ?? {}) }
}
